import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import { IgTextField, IgTypography } from "@intelligems/ig-theme";
import { ScrollDepthCustomEvent } from "@intelligems/ig-types";
import { Slider, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { CustomEventPageTargetingWrapper } from "@src/features/customEvents/components/CustomEventPageTargetingWrapper";
import {
  selectedCurrentCustomEvent,
  setCustomEventScrollDepth,
} from "@src/store/slices/customEvents";

interface ScrollDepthProps {
  isArchived: boolean;
}

export const ScrollDepth = ({ isArchived }: ScrollDepthProps) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const currentEvent = useSelector(selectedCurrentCustomEvent);
  const [sliderValue, setSliderValue] = useImmer<number>(
    currentEvent?.type === "scrollDepth" && currentEvent?.id
      ? (currentEvent as ScrollDepthCustomEvent).settings.desktop.percentage
      : 50
  );

  const handleChange = (e: Event, newValue: number) => {
    setSliderValue(Number(newValue));
  };

  const formatLabel = useCallback((num: number) => {
    return (
      <IgTypography color={"white"} variant={"body2"}>{`${num}%`}</IgTypography>
    );
  }, []);

  useEffect(() => {
    dispatch(setCustomEventScrollDepth({ scrollDepth: sliderValue }));
  }, [sliderValue]);

  return (
    <Stack width={"100%"}>
      <Grid
        container
        wrap={"nowrap"}
        alignItems={"center"}
        justifyContent={"space-between"}
        marginTop={"30px"}
        marginBottom={"30px"}
      >
        <Slider
          value={sliderValue}
          onChange={handleChange}
          disabled={isArchived}
          size={"small"}
          valueLabelDisplay={"on"}
          valueLabelFormat={formatLabel}
          sx={{
            width: "80%",
            "& .MuiSlider-thumb": {
              width: 15,
              height: 15,
              backgroundColor: theme.igPrimitives.gray[50],
              // eslint-disable-next-line ig-fe-requirements/no-hardcoded-hex-colors -- alpha/transparent; no ig-theme token
              border: "1px solid #0000001A",
              boxShadow: theme.igShadows.low,
            },
            "& .MuiSlider-track": {
              height: 4,
              borderRadius: 2,
              backgroundColor:
                theme.igBackgroundColors.interactive.accent.default,
            },
            "& .MuiSlider-rail": {
              height: 4,
              borderRadius: 2,
              backgroundColor:
                theme.igBackgroundColors.interactive.accent.disabled,
            },
            "& .MuiSlider-valueLabel": {
              borderRadius: "6px",
              padding: "4px 6px",
              backgroundColor: theme.palette.text.primary,
            },
          }}
        />
        <IgTextField
          value={sliderValue}
          slotProps={{
            input: {
              endAdornment: "%",
            },
          }}
          disabled={isArchived}
          sx={{
            width: "15%",
            "& .MuiInputBase-input": {
              textAlign: "left",
            },
            "& .Mui-disabled": {
              backgroundColor: theme.igPrimitives.gray[300],
            },
          }}
          onChange={(e) => setSliderValue(Number(e.target.value))}
        />
      </Grid>
      <CustomEventPageTargetingWrapper
        buttonText={"Add a Page"}
        isArchived={isArchived}
        showHeader
      />
    </Stack>
  );
};
